// ABOUTME: Guards the PWA silent auto-update against infinite reload loops.
// ABOUTME: Counts recent update-reloads (sessionStorage) so a non-sticking update can't spin forever.

// Update-reloads within this window count toward the loop cap. sessionStorage
// (not localStorage) so the log clears when the tab closes and never blocks a
// legitimate update in a future session.
export const RELOAD_LOOP_WINDOW_MS = 60_000
// More than this many update-reloads inside the window → the update isn't
// sticking; stop applying it silently and fall back to the manual indicator.
export const MAX_SILENT_RELOADS = 2

export const UPDATE_RELOAD_LOG_KEY = "pwa_update_reload_log"

// Fallback for environments where sessionStorage is unavailable (private mode,
// some embedded webviews) — still caps the loop within the tab's lifetime.
let memoryLog: number[] = []

const withinWindow = (now: number) => (ts: number) => now - ts < RELOAD_LOOP_WINDOW_MS

const readLog = (now: number): number[] => {
  let raw: string | null
  try {
    raw = sessionStorage.getItem(UPDATE_RELOAD_LOG_KEY)
  } catch {
    return memoryLog.filter(withinWindow(now))
  }
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((ts): ts is number => typeof ts === "number").filter(withinWindow(now))
  } catch {
    return []
  }
}

const writeLog = (log: number[]): void => {
  memoryLog = log
  try {
    sessionStorage.setItem(UPDATE_RELOAD_LOG_KEY, JSON.stringify(log))
  } catch {
    // sessionStorage unavailable — memoryLog already holds the value.
  }
}

// How many update-reloads happened inside the recent window.
export const recentUpdateReloadCount = (now: number = Date.now()): number => readLog(now).length

// Record that we are about to apply an update and reload the page.
export const recordUpdateReload = (now: number = Date.now()): void => {
  writeLog([...readLog(now), now])
}

// True when update-reloads have hit the cap — the update isn't sticking, so the
// caller must stop auto-applying and leave it to the manual update indicator.
export const isUpdateReloadLoop = (now: number = Date.now()): boolean =>
  recentUpdateReloadCount(now) >= MAX_SILENT_RELOADS

// Test-only: clear the in-memory fallback between cases.
export const __resetUpdateLoopGuardMemory = (): void => {
  memoryLog = []
}
